/* eslint-disable @next/next/no-img-element */
import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  threadDetailActions,
  selectPostById,
  selectDraftPost,
  selectThreadDetailOp,
  selectThreadDetailPosts,
} from 'src/redux/slices/threadDetailSlice';
import {
  DraftPostType, Mention, OpPost, PostState,
} from 'src/types/DiscussionPost';
import { AppState } from 'src/redux/store';
import {
  CustomEditor, ElementTypes, QuoteElement, MarkHtmlTypes,
} from 'src/types/CustomSlateTypes';
import { Transforms } from 'slate';
import QuoteIcon from 'public/images/icons/quote.svg';
import { useTranslation } from 'src/utils';
import { QuoteRef } from 'src/types/Quotes';
import { v4 as uuid } from 'uuid';
import DOMPurify from 'dompurify';
import { ProfileStatus } from 'src/types/UserInfo';

const QuotesWrapper = (
  { children, customEditor }:
  { children: (quoteRef: QuoteRef) => React.ReactNode,
    customEditor: CustomEditor
  },
) => {
  const markTypes: string[] = Object.values(MarkHtmlTypes);
  const quoteRefs: QuoteRef = { selectable: {}, nonSelectable: {} };
  const dispatch = useAppDispatch();
  const [selectionCoordinates, setSelectionCoordinates] = useState({ left: 0, top: 0 });
  const [isQuoteVisible, setIsQuoteVisible] = useState(false);
  const [insertQuote, setInsertQuote] = useState(false);
  const [quote, setQuote] = useState<{ postId: string, content: HTMLElement | undefined }>({ postId: '', content: undefined });
  const quotedPost = useAppSelector(
    (state: AppState) => selectPostById(state, quote.postId),
  );
  const opPost = useAppSelector<OpPost>(selectThreadDetailOp);
  const draftPost = useAppSelector(selectDraftPost);
  const threadDetailPosts = useAppSelector(selectThreadDetailPosts);
  const profileStatus = useAppSelector(({ userInfo }: AppState) => userInfo.profileStatus);
  const getSelectableRefs = () => quoteRefs.selectable;
  const getNonSelectableRefs = () => quoteRefs.nonSelectable;
  const postsRef = useRef(threadDetailPosts);
  postsRef.current = threadDetailPosts;
  const { t } = useTranslation();

  const validateDomIsSafe = (selectionElement: HTMLElement, postId: string) => {
    const posts = postsRef.current;
    if (!posts[postId]
      || !posts[postId].postDetails
      || !posts[postId].postDetails.content
    ) return false;
    const { content, contentMetadata } = posts[postId].postDetails;
    const purifyOptions = { ALLOWED_TAGS: ['img', 'a'], FORBID_ATTR: ['data-mentions'], RETURN_DOM: true };
    const cloneSelectionElement = document.createElement('div');
    cloneSelectionElement.appendChild(selectionElement.cloneNode(true));
    // Check if mentions is valid
    const mentionElements = cloneSelectionElement.querySelectorAll('[data-mentionid]');
    if (mentionElements && mentionElements.length > 0) {
      const isValid = Array.from(mentionElements).every((mentionElement) => {
        const mentionId = mentionElement.getAttribute('data-mentionid');
        const mentionUserId = mentionElement.getAttribute('data-mention-userid') || undefined;
        const mentionUsername = mentionElement.getAttribute('data-mention-username') || undefined;
        const contentMetadataMention: Mention | undefined = contentMetadata
        && contentMetadata.mentions
        && contentMetadata.mentions[mentionId!];
        return contentMetadataMention && (contentMetadataMention.userId === mentionUserId
        && mentionUsername === contentMetadataMention.username);
      });
      if (!isValid) return false;
    }

    // Remove mentions before running it with domPurify
    ['[data-mentionid]', '[data-mentions-link]'].forEach((attributeToRemove) => {
      const elementsToRemove = cloneSelectionElement.querySelectorAll(attributeToRemove);
      if (elementsToRemove && elementsToRemove.length > 0) {
        elementsToRemove.forEach((elemenToRemove) => {
          elemenToRemove.remove();
        });
      }
    });

    const stripedSelectionText = DOMPurify.sanitize(
      cloneSelectionElement,
      purifyOptions,
    ) as DocumentFragment;
    const stripedPostContent = DOMPurify.sanitize(content!, purifyOptions) as DocumentFragment;

    const stripedPostContentNodes = Array.from(stripedPostContent.childNodes);
    const stripedSelectionTextNodes = Array.from(stripedSelectionText.childNodes);
    const isValid = stripedSelectionTextNodes.every((selectionChildNode) => {
      if (selectionChildNode.nodeType === 3) {
        return stripedPostContentNodes.some(
          (contentChildNode) => contentChildNode.textContent!.includes(
            selectionChildNode.textContent as string,
          ),
        );
      }
      return stripedPostContentNodes.some(
        (contentChildNode) => contentChildNode.isEqualNode(selectionChildNode),
      );
    });

    return isValid;
  };

  const cleanQuoteContent = (element: HTMLDivElement) => DOMPurify.sanitize(
    element,
    { FORBID_TAGS: ['button'], RETURN_DOM: true },
  );
  const isChildNode = (node: Node) => (
    markTypes.includes(node.nodeName)
    || node.nodeType === Node.TEXT_NODE
  );
  const selectionHandler = (event: Event) => {
    if (typeof window.getSelection !== 'undefined') {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 1) sel.removeAllRanges();
      if (sel && sel.rangeCount === 1) {
        if (!sel.getRangeAt(0).cloneContents().hasChildNodes()) {
          setIsQuoteVisible(false);
          return;
        }
        const commonAncestor = sel.getRangeAt(0).commonAncestorContainer;
        const originalPost = Object.entries(getSelectableRefs()).find(
          (postContentRef) => postContentRef[1] && postContentRef[1].contains(commonAncestor),
        );

        const containsNonSelectable = Object.values(
          getNonSelectableRefs(),
        ).some((value) => value
        && !!value.nodeType
        && sel.getRangeAt(0).intersectsNode(value));
        if (!originalPost || containsNonSelectable) {
          setIsQuoteVisible(false);
          return;
        }

        let selectionElement;
        if (sel.getRangeAt(0).endContainer.parentElement!.getAttribute('data-mention-username') && event.type === 'mouseup') {
          const tempRange = document.createRange();
          tempRange.setStart(sel.getRangeAt(0).startContainer, sel.getRangeAt(0).startOffset);
          tempRange.setEnd(sel.getRangeAt(0).endContainer.parentNode as Node, 1);
          sel.removeAllRanges();
          sel.addRange(tempRange);
        }

        if (sel.getRangeAt(0).startContainer.parentElement!.getAttribute('data-mention-username') && event.type === 'mouseup') {
          const tempRange = document.createRange();
          tempRange.setStart(sel.getRangeAt(0).startContainer.parentNode as Node, 0);
          tempRange.setEnd(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset);
          sel.removeAllRanges();
          sel.addRange(tempRange);
        }

        let { commonAncestorContainer } = sel.getRangeAt(0);
        if (isChildNode(commonAncestorContainer)) {
          while (isChildNode(commonAncestorContainer)) {
            const tempNode = commonAncestorContainer.cloneNode();
            if (tempNode.nodeType !== Node.TEXT_NODE && selectionElement) {
              tempNode.appendChild(selectionElement);
              selectionElement = tempNode;
            } else {
              selectionElement = sel.getRangeAt(0).cloneContents();
            }
            if (commonAncestorContainer.parentElement) {
              commonAncestorContainer = commonAncestorContainer.parentElement;
            }
          }
        } else {
          selectionElement = commonAncestorContainer.cloneNode();
          selectionElement.appendChild(sel.getRangeAt(0).cloneContents());
        }
        if (selectionElement && selectionElement.hasChildNodes()) {
          const tempContainer = document.createElement('div');
          tempContainer.appendChild(selectionElement);
          if (!validateDomIsSafe(tempContainer, originalPost[0])) {
            setIsQuoteVisible(false);
            return;
          }
          setQuote({
            postId: originalPost[0],
            content: cleanQuoteContent(tempContainer).childNodes[0] as HTMLElement,
          });
          const { left, top } = sel.getRangeAt(0).getBoundingClientRect();
          setSelectionCoordinates({ left, top: top + document.documentElement.scrollTop - 33 });
          if (event.type === 'mouseup') {
            setIsQuoteVisible(true);
          }
          return;
        }
      }
    }
    setIsQuoteVisible(false);
  };

  useEffect(() => {
    if (insertQuote && quotedPost && quote.content && draftPost && draftPost.parentPostId) {
      const { postDetails } = quotedPost;
      const quoteId = `${postDetails.id}-${uuid()}`;
      const quoteElement: QuoteElement = {
        type: ElementTypes.quote,
        userId: postDetails.userId as string,
        username: postDetails.username,
        content: quote.content.innerHTML,
        avatarSrc: postDetails.avatarSrc,
        quoteId,
        children: [{ text: '' }],
        postId: postDetails.id,
      };
      Transforms.insertNodes(customEditor, quoteElement);
      setInsertQuote(false);
    }
  }, [insertQuote, draftPost?.parentPostId]);

  useEffect(() => {
    if (profileStatus === ProfileStatus.registered
      && !opPost.isLocked
      && opPost.postState === PostState.active
    ) {
      document.addEventListener('selectionchange', selectionHandler);
      document.addEventListener('mouseup', selectionHandler);
    }
    return () => {
      document.removeEventListener('selectionchange', selectionHandler);
      return document.removeEventListener('mouseup', selectionHandler);
    };
  }, []);

  const memoizedChildren = useMemo(
    () => children(quoteRefs),
    [
      ...Object.keys(quoteRefs.selectable),
      ...Object.keys(quoteRefs.nonSelectable),
    ],
  );

  const quoteButtonHandler = async () => {
    if (!draftPost) {
      await dispatch(threadDetailActions.setDraftPost({
        parentPostId: opPost.id,
        type: DraftPostType.reply,
      }));
      await dispatch(threadDetailActions.setDraftContent({}));
    }
    setInsertQuote(true);
    setIsQuoteVisible(false);
    window.getSelection()!.removeAllRanges();
  };
  const { left, top } = selectionCoordinates;
  const quoteButton = isQuoteVisible && createPortal(
    <button
      data-testid="quote-button"
      onClick={quoteButtonHandler}
      type="button"
      className="absolute z-auto cursor-pointer rounded-sm  border border-solid border-kat-casper bg-kat-snow  py-1.5 px-1 opacity-75"
      style={{ left, top }}
    >
      <img src={QuoteIcon.src} alt="quote" />
      <span className="text-[11px] font-bold text-kat-nordic ltr:ml-1 rtl:mr-1">{t('threadDetail_quote')}</span>
    </button>,
    document.querySelector('#seller-forums') as Element,
  );
  return (
    <>
      {memoizedChildren}
      {quoteButton}
    </>
  );
};

export default QuotesWrapper;
